<template>
  <div class="core_wrap hidden-sm-and-up">
    <div class="core_title">产品展示</div>

    <!-- 自定义Tab容器（移动端固定样式） -->
    <div class="custom-tabs core_tabs" :class="{ 'loading': loading }">
      <!-- 加载遮罩 -->
      <div class="loading-mask" v-if="loading">
        <div class="loading-spinner">
          <i class="el-icon-loading"></i>
        </div>
      </div>

      <!-- Tab头部（移动端固定布局：左箭头 + 文字 + 右箭头） -->
      <div class="tab-header">
        <!-- 左箭头 -->
        <div class="tab-arrow prev-arrow" @click="scrollTabLeft">
          <i class="el-icon-arrow-left"></i>
        </div>

        <!-- Tab列表容器（横向滚动，无滚动条）- 滚动核心容器 -->
        <div class="tab-list-wrap" ref="tabListWrap">
          <div class="tab-list">
            <!-- Tab项 -->
            <div
                class="tab-item"
                v-for="(item, index) in coresList"
                :key="index"
                :class="{ active: activeTabIndex === index }"
                @click="changeTab(index)"
            >
              {{ item.name }}
            </div>
          </div>
        </div>

        <!-- 右箭头 -->
        <div class="tab-arrow next-arrow" @click="scrollTabRight">
          <i class="el-icon-arrow-right"></i>
        </div>
      </div>

      <!-- Tab内容区域（移动端固定样式） -->
      <div class="tab-content">
        <div
            class="tab-pane"
            v-for="(item, index) in coresList"
            :key="index"
            v-show="activeTabIndex === index"
        >
          <!-- 底层背景图容器（错位基础：底层、大尺寸、轻微偏移） -->
          <div class="base-img-container">
            <img
                class="base_img"
                src="../../../assets/img/home/productshow/project_down.png"
                alt="产品背景"
                loading="lazy"
            />
          </div>

          <!-- 上层轮播图容器（错位核心：上层、小尺寸、反向偏移，与底图形成错位） -->
          <div class="product-carousel-wrap">
            <!-- 空数据提示 -->
            <div class="carousel-empty" v-if="!item.second || !item.second.length">
              暂无轮播图片
            </div>
            <!-- 轮播组件 -->
            <el-carousel
                v-else
                class="product-carousel"
                height="230px"
                indicator-position="bottom"
                autoplay
                loop
                arrow="hover"
            >
              <el-carousel-item v-for="(imgItem, imgIndex) in item.second" :key="imgIndex">
                <div class="carousel-img-container">
                  <img
                      class="carousel-img"
                      :src="getImgUrl(imgItem.src)"
                      :alt="`${item.name}-轮播图${imgIndex+1}`"
                      loading="lazy"
                      @click="handleImgClick(imgItem)"
                      @error="handleImgError($event)"
                  />
                </div>
              </el-carousel-item>
            </el-carousel>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getProduct } from "@/api/home";

export default {
  name: "ProductShow",
  data() {
    return {
      loading: false, // 加载状态
      coresList: [],  // 产品列表数据
      fallbackImg: require('@/assets/img/home/productshow/banner.png'), // 兜底图
      activeTabIndex: 0, // 当前选中的Tab索引
      tabScrollStep: 150 // Tab横向滚动步长（移动端固定值）
    };
  },
  created() {
    this.fetchProductList(); // 初始化加载数据
  },
  methods: {
    /**
     * 请求后端接口，获取产品列表
     */
    async fetchProductList() {
      try {
        this.loading = true;
        const res = await getProduct();
        if (res.code === 200) {
          this.coresList = (res.data || []).map(item => ({
            ...item,
            second: item.second || [] // 兜底空数组，防止报错
          }));
        } else {
          this.$message.error(res.msg || "获取产品列表失败");
          this.coresList = [];
        }
      } catch (error) {
        console.error("产品列表请求失败：", error);
        this.$message.error("网络异常，请稍后重试");
        this.coresList = [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * 切换Tab
     * @param {number} index 目标Tab索引
     */
    changeTab(index) {
      this.activeTabIndex = index;
      // 切换时自动滚动到当前Tab，优化体验
      const tabItem = this.$el.querySelector(`.tab-item:nth-child(${index + 1})`);
      if (tabItem && this.$refs.tabListWrap) {
        const wrap = this.$refs.tabListWrap;
        const offsetLeft = tabItem.offsetLeft - wrap.clientWidth / 2 + tabItem.clientWidth / 2;
        wrap.scrollTo({ left: offsetLeft, behavior: 'smooth' });
      }
    },

    /**
     * Tab列表向左滚动 - 修复滚动触发
     */
    scrollTabLeft() {
      if (this.$refs.tabListWrap) {
        // 确保滚动容器存在，使用scrollBy实现左滚
        this.$refs.tabListWrap.scrollBy({
          left: -this.tabScrollStep,
          behavior: 'smooth'
        });
      }
    },

    /**
     * Tab列表向右滚动 - 修复滚动触发
     */
    scrollTabRight() {
      if (this.$refs.tabListWrap) {
        // 确保滚动容器存在，使用scrollBy实现右滚
        this.$refs.tabListWrap.scrollBy({
          left: this.tabScrollStep,
          behavior: 'smooth'
        });
      }
    },

    /**
     * 处理图片URL（兼容绝对/相对路径）
     * @param {string} src 图片地址
     * @returns {string} 完整URL
     */
    getImgUrl(src) {
      if (!src) return this.fallbackImg;
      return src.startsWith("http") ? src : process.env.VUE_APP_BASE_API + src;
    },

    /**
     * 图片加载失败兜底
     * @param {Event} e 错误事件
     */
    handleImgError(e) {
      e.target.src = this.fallbackImg;
    },

    /**
     * 处理轮播图点击跳转
     * @param {Object} imgItem 图片项（包含url、urlType）
     */
    handleImgClick(imgItem) {
      const { url, urlType } = imgItem;
      try {
        // 1. 外部链接：新窗口打开
        if (urlType === '1') {
          // if (!/^https?:\/\//.test(url)) {
          //   this.$message.warning("外部链接格式错误，请检查");
          //   return;
          // }
          window.open(url, '_blank', 'noopener,noreferrer');
        }
        // 2. 内部路由：项目内跳转
        else if (urlType === '2') {
          this.$router.push({
            path: '/productDetail',
            query: {
              productId: imgItem.id,
              type: 'product'
            }
          });
        }
        // 其他类型：提示无跳转逻辑
        else {
          this.$message.error("暂无跳转链接");
        }
      } catch (error) {
        console.error("图片跳转失败：", error);
        this.$message.error("跳转失败，请稍后重试");
      }
    }
  }
};
</script>

<style lang="scss" scoped>
// 基础变量（移动端固定值）
$primary-color: #ce636c;
$text-color: #203040;
$bg-color: #f8f8f8ff;
$empty-color: #999;
$carousel-indicator-color: #fff;
$carousel-indicator-active-color: $primary-color;

@media screen and (max-width: 768px) {
  // 全局容器（移动端固定样式，无适配）
  .core_wrap {
    position: relative;
    width: 100%;
    text-align: center;
    background-color: $bg-color;
    box-sizing: border-box;
    padding: 0 15px;
    overflow: hidden; // 确保全局无滚动条

    // 标题样式（移动端固定）
    .core_title {
      color: $text-color;
      font-size: 1.2rem;
      font-weight: bold;
      text-align: left;
      padding: 40px 0 0 10px;
    }

    // 自定义Tab容器（移动端固定样式，核心）
    .custom-tabs {
      font-size: 16px;
      padding-left: 10px;
      padding-top: 20px;
      position: relative;
      width: 100%;
      overflow: hidden; // 无滚动条

      // 加载状态遮罩（移动端固定）
      &.loading {
        .loading-mask {
          display: flex;
        }
      }

      .loading-mask {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.8);
        display: none;
        align-items: center;
        justify-content: center;
        z-index: 999;

        .loading-spinner {
          font-size: 20px;
          color: $primary-color;
          animation: el-loading-rotate 2s linear infinite;
        }
      }

      // Tab头部（移动端固定布局：左箭头 + 文字 + 右箭头，核心）
      .tab-header {
        display: flex;
        align-items: center; // 容器整体垂直居中
        justify-content: center;
        width: 100%;
        overflow: hidden; // 无滚动条
        height: 40px; // 固定头部高度，辅助垂直居中

        // Tab箭头样式（移动端固定尺寸，左右各一个）
        .tab-arrow {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center; // 箭头图标垂直居中
          justify-content: center;
          background: rgba(255, 255, 255, 0.9);
          border-radius: 4px;
          cursor: pointer;
          color: $text-color;
          flex-shrink: 0; // 不被挤压
          font-size: 14px;

          &.prev-arrow {
            margin-right: 5px;
          }

          &.next-arrow {
            margin-left: 5px;
          }

          &:hover {
            color: $primary-color;
            background: #fff;
          }
        }

        // Tab列表容器 - 滚动核心！！！修复滑动的关键样式
        .tab-list-wrap {
          flex: 1; // 核心：占据箭头之间的所有空间
          overflow-x: auto; // 横向滚动 - 必须保留！
          overflow-y: hidden; // 垂直无滚动条 - 必须保留！
          -webkit-overflow-scrolling: touch; // 移动端顺滑滚动 - 必须保留！
          padding: 0; // 移除上下padding，不影响居中
          // 关键：移除height:100%，避免高度约束影响滚动
          // height: 100%;

          &::-webkit-scrollbar {
            display: none; // 强制隐藏滚动条，核心要求 - 保留！
          }

          // Tab列表 - 滚动内容容器！！！修复滑动的关键
          .tab-list {
            display: flex;
            white-space: nowrap;
            // 关键1：移除overflow: hidden！！！这是滑动失效的根本原因！
            // overflow: hidden;
            align-items: center; // 列表内项垂直居中 - 保留（文字居中）
            // 关键2：移除height:100%，避免高度约束影响滚动
            // height: 100%;
            min-width: 100%; // 确保列表宽度至少和容器一致
          }

          // Tab项样式（移动端固定，核心：修复垂直居中 + 不影响滚动）
          .tab-item {
            font-size: 16px;
            text-align: center;
            // 对称内边距，底部多2px给下划线 - 保留（文字居中）
            padding: 8px 8px 10px 8px;
            margin-right: 10px;
            cursor: pointer;
            color: $text-color;
            opacity: 0.8;
            flex-shrink: 0; // 不被挤压 - 必须保留（保证Tab项宽度）
            white-space: nowrap; // 不换行 - 必须保留
            // 文字垂直居中 - 保留（不影响滚动）
            display: flex;
            align-items: center;
            justify-content: center;

            // 选中态（移动端固定样式）
            &.active {
              color: $primary-color;
              opacity: 1;
              position: relative;

              &::after {
                content: '';
                position: absolute;
                bottom: 6px; // 适配padding
                left: 0;
                width: 100%;
                height: 2px;
                background-color: #f6f6f6;
              }
            }

            // 悬浮态
            &:hover {
              color: $primary-color;
              opacity: 1;
            }
          }
        }
      }

      // Tab内容区域（移动端固定样式）
      .tab-content {
        width: 100%;
        overflow: hidden; // 无滚动条
      }
    }

    // 核心：底层背景图容器（错位感基础，移动端固定样式）
    .base-img-container {
      width: 90%;
      margin: 15px auto 0;
      display: block;
      position: relative; // 定位基础
      z-index: 1; // 底层，低于轮播图
      overflow: hidden; // 无滚动条，核心要求
      left: 5%; // 轻微右偏移，为错位感做准备
      top: 10px; // 轻微下偏移

      // 底图样式（移动端固定：覆盖容器，无挤压，无滚动条）
      .base_img {
        width: 100%;
        height: 100%;
        object-fit: cover; // 保持比例，覆盖容器，无挤压
        object-position: center; // 居中显示
        display: block;
        overflow: hidden; // 无滚动条
      }
    }

    // 核心：上层轮播图容器（错位感核心，移动端固定样式）
    .product-carousel-wrap {
      position: relative; // 定位基础
      width: 90%;
      margin: -230px auto 0; // 核心：向上移动，覆盖在底图上方，形成错位
      z-index: 2; // 上层，高于底图，核心层级
      left: -5%; // 反向左偏移，与底图的右偏移形成错位，核心要求
      overflow: hidden; // 无滚动条，核心要求

      // 空数据提示（移动端固定样式）
      .carousel-empty {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: $empty-color;
        font-size: 14px;
        overflow: hidden; // 无滚动条
      }

      // 轮播组件核心样式（移动端固定）
      .product-carousel {
        width: 100%;
        height: 100%;
        overflow: hidden; // 无滚动条

        // 轮播图容器（移动端固定：确保图片无挤压，无滚动条）
        .carousel-img-container {
          width: 100%;
          height: 100%;
          overflow: hidden; // 无滚动条，核心要求
        }

        // 轮播图样式（移动端固定：包含在容器内，无挤压，无滚动条）
        .carousel-img {
          width: 100%;
          height: 100%;
          cursor: pointer;
          object-fit: contain; // 保持比例，完整包含在容器内，无挤压
          object-position: center; // 居中显示
          overflow: hidden; // 无滚动条
        }

        // 轮播指示器样式（移动端固定）
        ::v-deep .el-carousel__indicator {
          .el-carousel__indicator-button {
            background-color: rgba(255, 255, 255, 0.5);
            width: 10px;
            height: 10px;
            border-radius: 50%;
          }
        }

        ::v-deep .el-carousel__indicator.is-active {
          .el-carousel__indicator-button {
            background-color: $carousel-indicator-active-color;
          }
        }

        // 轮播箭头样式（移动端固定）
        ::v-deep .el-carousel__arrow {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background-color: rgba(0, 0, 0, 0.3);
          color: #fff;

          &:hover {
            background-color: $primary-color;
          }
        }
      }
    }
  }

  // 加载动画（移动端固定）
  @keyframes el-loading-rotate {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
}
</style>